import { Body, Box, Button, Icon, Sheet, VStack } from '@hover/blueprint';
import { iEdit2, iTrash2 } from '@hover/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';

import { DestructiveConfirmation } from 'src/modules/homeownerApp/components/DestructiveConfirmation';
import { MenuLinkDivider } from 'src/modules/homeownerApp/components/MenuLink';
import {
  useDeleteProjectClickHandler,
  useErrors,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';

const ProjectMoreActions = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const history = useHistory();

  const { setCurrentMenu } = useHomeownerAppContext();
  const { showError } = useErrors();

  const [step, setStep] = useState('more-actions');

  const { params } = useRouteMatch(Routes.HOMEOWNER_APP_PROJECT) ?? {
    params: {},
  };

  const { projectId } = params as { projectId: string };

  const [handleDeleteClick, deleteResult] = useDeleteProjectClickHandler(
    Number(projectId),
  );

  const formatError = useCallback(
    (errorMessage: string): string =>
      t('projects.confirmation.delete.error', { errorMessage }),
    [t],
  );

  useEffect(() => {
    if (deleteResult.error) {
      setCurrentMenu(null);
      showError({ error: deleteResult.error as Error, formatError });
    }
    if (deleteResult.data) {
      setCurrentMenu(null);
      history.push(Routes.HOMEOWNER_APP_PROJECTS_LIST.path);
    }
  }, [
    deleteResult.data,
    deleteResult.error,
    formatError,
    history,
    setCurrentMenu,
    showError,
  ]);

  const options = [
    {
      title: t('projects.delete'),
      onClick: () => setStep('delete-confirmation'),
      color: 'danger500',
      icon: iTrash2,
    },
    {
      title: t('projects.editDetails'),
      onClick: () => setCurrentMenu(HomeownerAppMenuNames.EDIT_PROJECT),
      icon: iEdit2,
    },
  ];

  return (
    <>
      {step === 'more-actions' && (
        <Sheet.Contents contentKey="more-actions">
          <VStack
            marginBottom={{ base: 300, tablet: 0 }}
            marginX={{ tablet: 500 }}
            marginY={{ tablet: 400 }}
          >
            {options.map(({ title, color, onClick, icon }, index) => (
              <Box key={title} width="100%">
                <Box
                  color={color}
                  cursor="pointer"
                  flexDirection="column"
                  width="100%"
                >
                  {index !== 0 && (
                    <MenuLinkDivider key={`divider-${title}`} mb={200} />
                  )}
                  <Box
                    alignItems="center"
                    justifyContent="space-between"
                    key={title}
                    onClick={onClick}
                    width="100%"
                  >
                    <Body color={color} isBold size={500}>
                      {title}
                    </Body>
                    <Icon icon={icon} />
                  </Box>
                </Box>
              </Box>
            ))}
            <Box display={{ tablet: 'none' }} paddingTop={300} width="100%">
              <Button
                onClick={() => setCurrentMenu(null)}
                variant="tertiary"
                width="100%"
              >
                {t('projects.edit.cancel')}
              </Button>
            </Box>
          </VStack>
        </Sheet.Contents>
      )}
      {step === 'delete-confirmation' && (
        <DestructiveConfirmation
          deleteAction={handleDeleteClick}
          deleteText={t('projects.confirmation.delete.delete')}
          headerText={t('projects.confirmation.delete.header')}
          subHeaderText={t('projects.confirmation.delete.confirmationText')}
        />
      )}
    </>
  );
};

export { ProjectMoreActions };
